import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AlertService } from '../../common/alert.service';
import { BehaviorSubject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {

  private timeoutMs = 15 * 60 * 1000; // 15 minutes
  private warningMs = 1 * 60 * 1000;  // show warning 1 minute before 
  private timer: any;
  private warningShown = false;
  private warningTimer: any;

  private excludedRoutes = ['/login'];

  private countdownVisible$ = new BehaviorSubject<boolean>(false);
  private timeLeft$ = new BehaviorSubject<number>(this.timeoutMs);
  private intervalId: any;
  private lastActivityTimestamp = Date.now();

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private alert: AlertService,
    private dialog: MatDialog,
    private authService: AuthService
  ) { }

  startMonitoring() {
    this.resetTimer();
    this.listenToUserActivity();
  }

  private listenToUserActivity() {

    window.addEventListener('keydown', (event: KeyboardEvent) => {
      const isCombo = event.ctrlKey && event.altKey && event.key.toLowerCase() === 't';
      if (isCombo) {
        const current = this.countdownVisible$.getValue();
        this.countdownVisible$.next(!current);
      }
    });


    ['click', 'keydown', 'mousemove', 'touchstart'].forEach(event =>
      window.addEventListener(event, () => this.resetTimer())
    );
  }

  private resetTimer() {
    clearTimeout(this.timer);
    clearTimeout(this.warningTimer); // ✅ Clear previous warning timer
    this.warningShown = false;

    const currentUrl = this.getNormalizedUrl();
    if (this.excludedRoutes.includes(currentUrl)) {
      return;
    }

    this.lastActivityTimestamp = Date.now();

    // Main timeout
    this.ngZone.runOutsideAngular(() => {
      this.timer = setTimeout(() => {
        this.ngZone.run(() => this.handleTimeout());
      }, this.timeoutMs);
    });

    this.startCountdownInterval();

    // Single warning timer
    this.warningTimer = setTimeout(() => {
      if (!this.warningShown) {
        this.warningShown = true;
        const warningMessage = '🕒 Session about to expire soon...';
        console.warn(warningMessage);
        this.alert.warning(warningMessage);
      }
    }, this.timeoutMs - this.warningMs);
  }

  private startCountdownInterval() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.intervalId = setInterval(() => {
      const remaining = this.timeoutMs - (Date.now() - this.lastActivityTimestamp);
      this.timeLeft$.next(Math.max(remaining, 0));
    }, 1000);
  }

  private async handleTimeout() {
    // no timer on excluded routes
    const currentUrl = this.getNormalizedUrl();
    if (this.excludedRoutes.includes(currentUrl)) return;

    const timeoutMessage = '🚫 Session expired due to inactivity';
    console.warn(timeoutMessage);
    this.alert.error(timeoutMessage);

    this.dialog.closeAll();

    // Send them to login with a reason
    await this.authService.logout('/login?reason=timeout');
  }

  manualReset() {
    this.resetTimer();
  }

  private getNormalizedUrl(): string {
    return this.router.url.split('?')[0]; //removes any parameters
  }

  getTimeLeft$() {
    return this.timeLeft$.asObservable();
  }

  getCountdownVisible$() {
    return this.countdownVisible$.asObservable();
  }

  /** Inject server-provided timeouts before starting monitoring */
  public setSettings(timeoutMs: number, warningMs: number): void {
    // basic guards
    if (timeoutMs > 0) this.timeoutMs = timeoutMs;
    if (warningMs > 0 && warningMs < this.timeoutMs) {
      this.warningMs = warningMs;
    } else {
      // ensure warning is always < timeout
      this.warningMs = Math.max(1_000, Math.floor(this.timeoutMs / 10));
    }

    // Reset the countdown source to reflect the new timeout on first paint
    this.timeLeft$.next(this.timeoutMs);
  }

  /** Use when an admin updates settings and you want live effect */
  public reconfigureAndReset(timeoutMs: number, warningMs: number): void {
    this.setSettings(timeoutMs, warningMs);
    this.manualReset();
  }
}
